"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRoles, createRole, updateRole, deleteRole } from "@/state/roles/roles.slice";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Shield, PencilSimple, Trash } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

// Available permissions in the system
const AVAILABLE_PERMISSIONS = [
    { id: "Dashboard", label: "Dashboard", description: "Access to main dashboard" },
    { id: "ViewDashboardStats", label: "View Dashboard Stats", description: "View financial statistics on dashboard" },
    { id: "Sales", label: "Sales", description: "Create and manage sales" },
    { id: "ViewAllSales", label: "View All Sales", description: "View sales from all users" },
    { id: "ViewSalesSummary", label: "View Sales Summary", description: "See the totals, revenue, profit and outstanding cards on the Sales pages" },
    { id: "EditSalePrice", label: "Edit Sale Price", description: "Override a product's price while making a sale (custom price)" },
    { id: "Products", label: "Products (View)", description: "View products catalog (read-only)" },
    { id: "AddProducts", label: "Add Products", description: "Create new products" },
    { id: "EditProducts", label: "Edit Products", description: "Modify existing products" },
    { id: "DeleteProducts", label: "Delete Products", description: "Delete products from catalog" },
    { id: "ViewCostPrice", label: "View Cost Price", description: "View product cost/buying prices" },
    { id: "Users", label: "Users", description: "Manage system users" },
    { id: "Reports", label: "Reports", description: "View and generate reports" },
    { id: "Settings", label: "Settings", description: "System settings access" },
    { id: "Expenses", label: "Expenses", description: "Manage expenses" },
    { id: "Customers", label: "Customers", description: "Manage customers" },
    { id: "Stock", label: "Inventory", description: "Manage stock/inventory" },
    { id: "Returns", label: "Returns", description: "Process returns" },
    { id: "ProcessCrossShopReturns", label: "Process Cross-Shop Returns", description: "Process returns for sales from other shops" },
    { id: "AcceptPayments", label: "Accept Payments", description: "Accept payments for credit sales" },
    { id: "AcceptCrossShopPayments", label: "Accept Cross-Shop Payments", description: "Accept payments for sales from other shops" },
    { id: "ManagePayments", label: "Manage Payments", description: "Edit or delete payment records" },
    { id: "Purchases", label: "Purchases", description: "Manage supplier purchases" },
    { id: "ManageSurplusSales", label: "Manage Surplus Sales", description: "Record and manage surplus sales amounts" },
];

export default function RoleManagement() {
    const dispatch = useDispatch();
    const { toast } = useToast();

    const { roles, isLoading } = useSelector((state) => state.roles);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        name: "",
        permissions: [],
    });

    useEffect(() => {
        dispatch(fetchRoles());
    }, [dispatch]);

    // Reset form when editing role changes
    useEffect(() => {
        if (editingRole) {
            setFormData({
                name: editingRole.name || "",
                permissions: typeof editingRole.permissions === "string" ? JSON.parse(editingRole.permissions) : (editingRole.permissions || []),
            });
        } else {
            setFormData({ name: "", permissions: [] });
        }
    }, [editingRole]);

    const handleAddRole = useCallback(() => {
        setEditingRole(null);
        setFormData({ name: "", permissions: [] });
        setIsFormOpen(true);
    }, []);

    const handleEditRole = useCallback((role) => {
        setEditingRole(role);
        setIsFormOpen(true);
    }, []);

    const handleDeleteClick = useCallback((role) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!roleToDelete) return;

        try {
            await dispatch(deleteRole(roleToDelete.id)).unwrap();
            toast({ title: "Success", description: "Role deleted successfully" });
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: error });
        } finally {
            setDeleteDialogOpen(false);
            setRoleToDelete(null);
        }
    }, [dispatch, roleToDelete, toast]);

    const handlePermissionToggle = useCallback((permissionId) => {
        setFormData((prev) => ({
            ...prev,
            permissions: prev.permissions.includes(permissionId)
                ? prev.permissions.filter((p) => p !== permissionId)
                : [...prev.permissions, permissionId],
        }));
    }, []);

    const handleSelectAll = useCallback(() => {
        const allIds = AVAILABLE_PERMISSIONS.map((p) => p.id);
        setFormData((prev) => ({
            ...prev,
            permissions: prev.permissions.length === allIds.length ? [] : allIds,
        }));
    }, []);

    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast({ variant: "destructive", title: "Error", description: "Role name is required" });
            return;
        }

        try {
            if (editingRole) {
                await dispatch(updateRole({ id: editingRole.id, roleData: formData })).unwrap();
                toast({ title: "Success", description: "Role updated successfully" });
            } else {
                await dispatch(createRole(formData)).unwrap();
                toast({ title: "Success", description: "Role created successfully" });
            }
            setIsFormOpen(false);
            setEditingRole(null);
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: error });
        }
    }, [dispatch, formData, editingRole, toast]);

    return (
        <div className="mx-auto p-4 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-2">
                        <Shield className="h-8 w-8" />
                        Role Management
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Create and manage roles with specific permissions
                    </p>
                </div>
                <Button onClick={handleAddRole} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Add Role
                </Button>
            </div>

            {/* Info Card */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg">How Permissions Work</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                    <ol className="list-decimal list-inside space-y-1">
                        <li><strong>Create a Role</strong> with a name (e.g., "Sales Manager")</li>
                        <li><strong>Assign Permissions</strong> to the role using checkboxes</li>
                        <li><strong>Assign Role to Users</strong> in User Management</li>
                        <li>Users can only access features their role permits</li>
                    </ol>
                </CardContent>
            </Card>

            {/* Roles Table */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : (
                <div className="border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/50">
                                <TableHead>Role Name</TableHead>
                                <TableHead>Permissions</TableHead>
                                <TableHead className="w-[100px]">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {roles?.map((role) => {
                                const perms = typeof role.permissions === "string" ? JSON.parse(role.permissions) : (role.permissions || []);
                                return (
                                    <TableRow key={role.id}>
                                        <TableCell className="font-medium">{role.name}</TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {perms.slice(0, 5).map((perm) => (
                                                    <Badge key={perm} variant="secondary" className="text-xs">
                                                        {perm}
                                                    </Badge>
                                                ))}
                                                {perms.length > 5 && (
                                                    <Badge variant="outline" className="text-xs">
                                                        +{perms.length - 5} more
                                                    </Badge>
                                                )}
                                                {perms.length === 0 && (
                                                    <span className="text-muted-foreground text-sm">No permissions</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleEditRole(role)}
                                                    // disabled={role.name === "Super Admin"}
                                                    title={role.name === "Super Admin" ? "Cannot edit Super Admin" : "Edit role"}
                                                >
                                                    <PencilSimple className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDeleteClick(role)}
                                                    // disabled={role.name === "Super Admin"}
                                                    className="text-red-500 hover:text-red-600"
                                                    title={role.name === "Super Admin" ? "Cannot delete Super Admin" : "Delete role"}
                                                >
                                                    <Trash className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                            {roles?.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                                        No roles found. Create your first role.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Form Dialog */}
            <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
                <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingRole ? "Edit Role" : "Create New Role"}</DialogTitle>
                        <DialogDescription>
                            {editingRole
                                ? "Update the role name and permissions."
                                : "Enter a role name and select the permissions this role should have."
                            }
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleFormSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="name">Role Name *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., Sales Manager"
                            />
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Label>Permissions</Label>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleSelectAll}
                                >
                                    {formData.permissions.length === AVAILABLE_PERMISSIONS.length
                                        ? "Deselect All"
                                        : "Select All"
                                    }
                                </Button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {AVAILABLE_PERMISSIONS.map((permission) => (
                                    <div
                                        key={permission.id}
                                        className="flex items-start space-x-3 p-3 border hover:bg-muted/50"
                                    >
                                        <Checkbox
                                            id={permission.id}
                                            checked={formData.permissions.includes(permission.id)}
                                            onCheckedChange={() => handlePermissionToggle(permission.id)}
                                        />
                                        <div>
                                            <label
                                                htmlFor={permission.id}
                                                className="text-sm font-medium cursor-pointer"
                                            >
                                                {permission.label}
                                            </label>
                                            <p className="text-xs text-muted-foreground">
                                                {permission.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : editingRole ? "Update Role" : "Create Role"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Role</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete the role{" "}
                            <strong>{roleToDelete?.name}</strong>? Users with this role will
                            lose their permissions.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmDelete}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
